import { Event, Execution } from '../../../api/scheduler-service';
import { SimulationParams } from './simulation-options/simulation-options.component';

/**
 * Per-execution view shape produced by the simulation pipeline. Both the
 * Gantt and the Table view consume this so the two views stay in lockstep —
 * if the math changes here, both views reflect the change automatically.
 */
export interface SimulatedExecution {
    /** Original execution from the server — kept verbatim for the "Original" column. */
    original: Execution;
    /** Position of this execution in the flattened (event, executionIndex) sequence —
     *  used as the seed for the deterministic-noise jitter / failure decision so
     *  a slider nudge doesn't re-shuffle which bars are red. */
    flatIndex: number;
    /** Post-drift, post-jitter start. Identical to `original.startTime` when sim is off. */
    start?: Date;
    /** Post-drift, post-jitter end. Identical to `original.endTime` when sim is off. */
    end?: Date;
    /** Width of [start, end] in ms. 0 when start or end is missing. */
    durationMs: number;
    /** Drift component applied (always equal to params.driftMs when sim is on). */
    driftAppliedMs: number;
    /** Jitter component applied for THIS execution. Signed (can be negative). */
    jitterAppliedMs: number;
    /** Painted-failure decision for this execution. */
    failed: boolean;
}

export interface SimulatedEvent {
    event: Event;
    /** Server-emitted "this is when the event would fire" anchor — preserved
     *  unchanged so the Table can show a "scheduled at" column independent of
     *  whatever drift/jitter pushed the actual run. */
    originalFireTime?: Date;
    /** All this event's executions, decorated with simulation values. */
    executions: SimulatedExecution[];
    /** Earliest simulated start across executions — convenience for sorting / parent bar. */
    simulatedStart?: Date;
    /** Latest simulated end across executions. */
    simulatedEnd?: Date;
}

/**
 * Decorate the raw events array with simulation values. Pure — no side-effects,
 * deterministic given the same (events, params) pair, so memoising in a signal
 * computed() works.
 */
export function decorateEvents(events: Event[], params: SimulationParams): SimulatedEvent[] {
    let flatIndex = 0;
    return (events ?? []).map(ev => {
        const executions = (ev.executions ?? []).map(exec => {
            const sim = applySimulation(exec.startTime, exec.endTime, params, flatIndex);
            const failed = isSimulatedFailure(params, flatIndex);
            const out: SimulatedExecution = {
                original: exec,
                flatIndex,
                start: sim.start,
                end: sim.end,
                durationMs: sim.durationMs,
                driftAppliedMs: sim.driftAppliedMs,
                jitterAppliedMs: sim.jitterAppliedMs,
                failed,
            };
            flatIndex += 1;
            return out;
        });
        return {
            event: ev,
            originalFireTime: toDate(ev.originalExecutionDateTime),
            executions,
            simulatedStart: minDefined(executions.map(e => e.start)),
            simulatedEnd:   maxDefined(executions.map(e => e.end)),
        };
    });
}

/**
 * Combine drift + duration override + jitter into a synthetic (start, end)
 * pair for one execution. When durationMs is 0 the caller is "previewing real
 * schedule output" — only drift applies, original endTime is preserved.
 */
export function applySimulation(rawStart: string | undefined, rawEnd: string | undefined,
                                params: SimulationParams, index: number)
        : { start?: Date; end?: Date; durationMs: number; driftAppliedMs: number; jitterAppliedMs: number } {
    const start = toDate(rawStart);
    if (!start) {
        const end = toDate(rawEnd);
        return { start: undefined, end, durationMs: 0, driftAppliedMs: 0, jitterAppliedMs: 0 };
    }
    const driftAppliedMs = params.driftMs || 0;
    const drifted = new Date(start.getTime() + driftAppliedMs);
    if (!params.durationMs || params.durationMs <= 0) {
        // No override — keep server end, only drift was applied.
        const end = toDate(rawEnd);
        const durationMs = end ? Math.max(0, end.getTime() - drifted.getTime()) : 0;
        return { start: drifted, end, durationMs, driftAppliedMs, jitterAppliedMs: 0 };
    }
    const jitterAppliedMs = params.jitterPercent
            ? params.durationMs * (params.jitterPercent / 100) * (deterministicNoise(index) - 0.5) * 2
            : 0;
    const ms = Math.max(1, Math.round(params.durationMs + jitterAppliedMs));
    return {
        start: drifted,
        end: new Date(drifted.getTime() + ms),
        durationMs: ms,
        driftAppliedMs,
        jitterAppliedMs: Math.round(jitterAppliedMs),
    };
}

/** True when execution #index should render as a simulated failure. Salted
 *  noise channel so failure pattern doesn't correlate with jitter pattern. */
export function isSimulatedFailure(params: SimulationParams, index: number): boolean {
    if (!params.failurePercent || params.failurePercent <= 0) return false;
    if (params.failurePercent >= 100) return true;
    return deterministicNoise(index, 0xa1b2c3) < (params.failurePercent / 100);
}

/** Splittable mix64 — stable [0, 1) noise per integer key. Stable so the user
 *  nudging a slider doesn't reshuffle which bars are red. */
export function deterministicNoise(index: number, salt: number = 0): number {
    let z = ((index + 1) * 0x9e3779b1 ^ salt) >>> 0;
    z = (z ^ (z >>> 16)) * 0x85ebca6b >>> 0;
    z = (z ^ (z >>> 13)) * 0xc2b2ae35 >>> 0;
    z = (z ^ (z >>> 16)) >>> 0;
    return (z & 0xffffffff) / 0xffffffff;
}

function toDate(value: string | undefined): Date | undefined {
    return value ? new Date(value) : undefined;
}

function minDefined(dates: (Date | undefined)[]): Date | undefined {
    const defined = dates.filter((d): d is Date => !!d);
    return defined.length ? new Date(Math.min(...defined.map(d => d.getTime()))) : undefined;
}

function maxDefined(dates: (Date | undefined)[]): Date | undefined {
    const defined = dates.filter((d): d is Date => !!d);
    return defined.length ? new Date(Math.max(...defined.map(d => d.getTime()))) : undefined;
}
